import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { tutorialProgressAPI } from '../services/portalAPI';
import { usePortalAuth } from './PortalAuthContext';
import type { TutorialStep, TutorialPage, TutorialProgress } from '../types/tutorial';
import tutorialData from '../components/Portal/Tutorial/tutorialData';

// Re-export types for convenience
export type { TutorialStep, TutorialPage, TutorialProgress };

// Context state
interface TutorialState {
  isEnabled: boolean;
  currentPageKey: string | null;
  currentStepIndex: number;
  isActive: boolean;  // Tutorial is currently running
  progress: TutorialProgress;
  loading: boolean;
}

// Context value
interface TutorialContextValue extends TutorialState {
  // Actions
  startTutorial: (pageKey: string) => void;
  nextStep: () => void;
  previousStep: () => void;
  skipTutorial: () => void;
  completeTutorial: (dismissed?: boolean) => void;
  resetTutorial: (pageKey: string) => void;
  resetAllTutorials: () => void;
  setCurrentPage: (pageKey: string) => void;

  // Getters
  getCurrentTutorial: () => TutorialPage | null;
  getCurrentStep: () => TutorialStep | null;
  isPageCompleted: (pageKey: string) => boolean;
  isPageDismissed: (pageKey: string) => boolean;
  shouldShowTutorial: (pageKey: string) => boolean;
  getTotalSteps: () => number;

  // Tutorial registry
  registerTutorial: (tutorial: TutorialPage) => void;
  getTutorialForPage: (pageKey: string) => TutorialPage | null;
}

const TutorialContext = createContext<TutorialContextValue | undefined>(undefined);

// Store registered tutorials
const tutorialRegistry = new Map<string, TutorialPage>();

export const TutorialProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = usePortalAuth();
  const hasRegistered = useRef(false);

  const [state, setState] = useState<TutorialState>({
    isEnabled: true,
    currentPageKey: null,
    currentStepIndex: 0,
    isActive: false,
    progress: {
      completed: [],
      dismissed: [],
      lastUpdated: null,
    },
    loading: true,
  });

  // Register all tutorials on mount (only once)
  useEffect(() => {
    if (!hasRegistered.current) {
      tutorialData.forEach((tutorial) => {
        tutorialRegistry.set(tutorial.pageKey, tutorial);
      });
      hasRegistered.current = true;
    }
  }, []);

  // Fetch progress from API on mount
  useEffect(() => {
    const fetchProgress = async () => {
      if (!isAuthenticated) {
        setState(prev => ({ ...prev, loading: false }));
        return;
      }

      try {
        const response = await tutorialProgressAPI.get();
        setState(prev => ({
          ...prev,
          progress: {
            completed: response.data.completed || [],
            dismissed: response.data.dismissed || [],
            lastUpdated: response.data.lastUpdated,
          },
          loading: false,
        }));
      } catch (error) {
        console.error('[Tutorial] Failed to fetch progress:', error);
        setState(prev => ({ ...prev, loading: false }));
      }
    };

    fetchProgress();
  }, [isAuthenticated]);

  // Register a tutorial
  const registerTutorial = useCallback((tutorial: TutorialPage) => {
    tutorialRegistry.set(tutorial.pageKey, tutorial);
  }, []);

  // Get tutorial for a page
  const getTutorialForPage = useCallback((pageKey: string): TutorialPage | null => {
    return tutorialRegistry.get(pageKey) || null;
  }, []);

  // Get current tutorial
  const getCurrentTutorial = useCallback((): TutorialPage | null => {
    if (!state.currentPageKey) return null;
    return tutorialRegistry.get(state.currentPageKey) || null;
  }, [state.currentPageKey]);

  // Get current step
  const getCurrentStep = useCallback((): TutorialStep | null => {
    const tutorial = getCurrentTutorial();
    if (!tutorial || state.currentStepIndex >= tutorial.steps.length) return null;
    return tutorial.steps[state.currentStepIndex];
  }, [getCurrentTutorial, state.currentStepIndex]);

  // Get total steps
  const getTotalSteps = useCallback((): number => {
    const tutorial = getCurrentTutorial();
    return tutorial?.steps.length || 0;
  }, [getCurrentTutorial]);

  // Check if page is completed
  const isPageCompleted = useCallback((pageKey: string): boolean => {
    return state.progress.completed.includes(pageKey);
  }, [state.progress.completed]);

  // Check if page is dismissed
  const isPageDismissed = useCallback((pageKey: string): boolean => {
    return state.progress.dismissed.includes(pageKey);
  }, [state.progress.dismissed]);

  // Check if should show tutorial for page
  const shouldShowTutorial = useCallback((pageKey: string): boolean => {
    if (!state.isEnabled) return false;
    if (isPageCompleted(pageKey)) return false;
    if (isPageDismissed(pageKey)) return false;

    const tutorial = tutorialRegistry.get(pageKey);
    if (!tutorial) return false;

    // Check role requirements
    if (tutorial.requiredRole && user) {
      // Get user role from permissions or similar
      // For now, assume all users can see tutorials
    }

    // Check customer type requirements
    if (tutorial.requiredCustomerType && user?.customer) {
      if (user.customer.customer_type !== tutorial.requiredCustomerType) {
        return false;
      }
    }

    return true;
  }, [state.isEnabled, isPageCompleted, isPageDismissed, user]);

  // Set current page (called when navigating)
  const setCurrentPage = useCallback((pageKey: string) => {
    setState(prev => ({
      ...prev,
      currentPageKey: pageKey,
      currentStepIndex: 0,
      isActive: false,
    }));
  }, []);

  // Start tutorial for current page
  const startTutorial = useCallback((pageKey: string) => {
    const tutorial = tutorialRegistry.get(pageKey);
    if (!tutorial || tutorial.steps.length === 0) return;

    setState(prev => ({
      ...prev,
      currentPageKey: pageKey,
      currentStepIndex: 0,
      isActive: true,
    }));
  }, []);

  // Go to next step
  const nextStep = useCallback(() => {
    const tutorial = getCurrentTutorial();
    if (!tutorial) return;

    if (state.currentStepIndex < tutorial.steps.length - 1) {
      setState(prev => ({
        ...prev,
        currentStepIndex: prev.currentStepIndex + 1,
      }));
    } else {
      // Last step - complete the tutorial
      completeTutorial(false);
    }
  }, [getCurrentTutorial, state.currentStepIndex]);

  // Go to previous step
  const previousStep = useCallback(() => {
    if (state.currentStepIndex > 0) {
      setState(prev => ({
        ...prev,
        currentStepIndex: prev.currentStepIndex - 1,
      }));
    }
  }, [state.currentStepIndex]);

  // Skip tutorial (marks as dismissed)
  const skipTutorial = useCallback(async () => {
    if (!state.currentPageKey) return;

    try {
      await tutorialProgressAPI.complete(state.currentPageKey, true);
      setState(prev => ({
        ...prev,
        isActive: false,
        progress: {
          ...prev.progress,
          completed: [...prev.progress.completed, state.currentPageKey!],
          dismissed: [...prev.progress.dismissed, state.currentPageKey!],
        },
      }));
    } catch (error) {
      console.error('[Tutorial] Failed to skip tutorial:', error);
      // Still close locally even if API fails
      setState(prev => ({ ...prev, isActive: false }));
    }
  }, [state.currentPageKey]);

  // Complete tutorial
  const completeTutorial = useCallback(async (dismissed: boolean = false) => {
    if (!state.currentPageKey) return;

    try {
      await tutorialProgressAPI.complete(state.currentPageKey, dismissed);
      setState(prev => ({
        ...prev,
        isActive: false,
        progress: {
          ...prev.progress,
          completed: [...prev.progress.completed, state.currentPageKey!],
          dismissed: dismissed
            ? [...prev.progress.dismissed, state.currentPageKey!]
            : prev.progress.dismissed,
        },
      }));
    } catch (error) {
      console.error('[Tutorial] Failed to complete tutorial:', error);
      setState(prev => ({ ...prev, isActive: false }));
    }
  }, [state.currentPageKey]);

  // Reset a tutorial
  const resetTutorial = useCallback(async (pageKey: string) => {
    try {
      await tutorialProgressAPI.reset(pageKey);
      setState(prev => ({
        ...prev,
        progress: {
          ...prev.progress,
          completed: prev.progress.completed.filter(k => k !== pageKey),
          dismissed: prev.progress.dismissed.filter(k => k !== pageKey),
        },
      }));
    } catch (error) {
      console.error('[Tutorial] Failed to reset tutorial:', error);
    }
  }, []);

  // Reset all tutorials
  const resetAllTutorials = useCallback(async () => {
    try {
      await tutorialProgressAPI.resetAll();
      setState(prev => ({
        ...prev,
        progress: {
          completed: [],
          dismissed: [],
          lastUpdated: null,
        },
      }));
    } catch (error) {
      console.error('[Tutorial] Failed to reset all tutorials:', error);
    }
  }, []);

  const value = useMemo<TutorialContextValue>(() => ({
    ...state,
    startTutorial,
    nextStep,
    previousStep,
    skipTutorial,
    completeTutorial,
    resetTutorial,
    resetAllTutorials,
    setCurrentPage,
    getCurrentTutorial,
    getCurrentStep,
    isPageCompleted,
    isPageDismissed,
    shouldShowTutorial,
    getTotalSteps,
    registerTutorial,
    getTutorialForPage,
  }), [
    state,
    startTutorial,
    nextStep,
    previousStep,
    skipTutorial,
    completeTutorial,
    resetTutorial,
    resetAllTutorials,
    setCurrentPage,
    getCurrentTutorial,
    getCurrentStep,
    isPageCompleted,
    isPageDismissed,
    shouldShowTutorial,
    getTotalSteps,
    registerTutorial,
    getTutorialForPage,
  ]);

  return (
    <TutorialContext.Provider value={value}>
      {children}
    </TutorialContext.Provider>
  );
};

export const useTutorial = (): TutorialContextValue => {
  const context = useContext(TutorialContext);
  if (!context) {
    throw new Error('useTutorial must be used within TutorialProvider');
  }
  return context;
};

export default TutorialContext;
